import React, { useState, useEffect, useRef } from "react";
import {
  Users,
  Plus,
  EyeOff,
  Phone,
  MessageCircle,
  Mail,
  X,
  ChevronDown,
} from "lucide-react";
import BossCreateEmployee from "./BossCreateEmployee";
import BossShowEmployee from "./BossShowEmployee";
import BossEmployeesNotActive from "./BossEmployeesNotActive";
import BossUpdateEmployeeModal from "./BossUpdateEmployeeModal";
import BossStatisticEmployeeData2 from "../BossStatisticComps/BossStatisticEmployeeData2";
import axios from "axios";
import { MyDomain } from "../../../App";
import { useTranslation } from "react-i18next";
import { useAppContext } from "../../../AppContext";

export default function MPBossEmployee() {
  const { t } = useTranslation();
  const { Lang } = useAppContext();
  const isRTL = Lang === "ar" || Lang === "he";
  const [modalVisible, setModalVisible] = useState(false);
  const [inactiveModalVisible, setInactiveModalVisible] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [choosedEmployee, setChoosedEmployee] = useState(null);
  const [loading, setLoading] = useState(false);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [showContactModal, setShowContactModal] = useState(false);
  const [userData, setUserData] = useState(null);
  const [allData, setAllData] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const statsRef = useRef(null);

  useEffect(() => {
    // Get user data from localStorage
    const user = localStorage.getItem("user");
    if (user) {
      setUserData(JSON.parse(user));
    }
  }, []);

  const checkSubscriptionStatus = (dateStr) => {
    if (!dateStr) return;

    const [day, month, year] = dateStr.split("-");
    const expiryDate = new Date(year, month - 1, day);

    const currentDate = new Date();
    currentDate.setHours(0, 0, 0, 0);
    expiryDate.setHours(0, 0, 0, 0);

    const diffTime = expiryDate - currentDate;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    const formattedDate = expiryDate.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });

    if (diffDays < 0) {
      setSubscriptionStatus({
        type: "expired",
        title: t("subscriptionExpired"),
        message: t("subscriptionExpiredMessage"),
        color: "#ef4444",
        icon: "❌",
      });
    } else if (diffDays === 0) {
      setSubscriptionStatus({
        type: "today",
        title: t("subscriptionExpiringToday"),
        message: t("subscriptionExpiringTodayMessage"),
        color: "#f97316",
        icon: "⚠️",
      });
    } else if (diffDays <= 7) {
      setSubscriptionStatus({
        type: "expiring-soon",
        title: t("subscriptionExpiringSoon"),
        message: `Your subscription expires in ${diffDays} day${
          diffDays === 1 ? "" : "s"
        } on ${formattedDate}`,
        color: "#f59e0b",
        icon: "⏰",
        daysRemaining: diffDays,
      });
    } else {
      setSubscriptionStatus(null);
    }
  };

  const fetchData = async () => {
    if (!userData) return;

    setLoading(true);
    try {
      const response = await axios.post(`${MyDomain}Employee/GetAllData`, {
        UserId: userData.id,
      });
      console.log(response.data);
      setAllData(response.data || []);

      // Check subscription status
      if (
        response.data.mySubscription &&
        response.data.mySubscription.length > 0
      ) {
        const lastSubscription = [...response.data.mySubscription].sort(
          (a, b) => b.id - a.id,
        )[0];
        checkSubscriptionStatus(lastSubscription.dateEnd);
      }
    } catch (err) {
      console.error("❌ Error fetching employees:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (userData) {
      fetchData();
    }
  }, [userData]);

  useEffect(() => {
    if (selectedEmployee && statsRef.current) {
      statsRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [selectedEmployee]);

  const handleEmployeeClick = (employee) => {
    setSelectedEmployee(employee);
  };

  const handleBack = () => {
    setSelectedEmployee(null);
    const cached = localStorage.getItem("allData");
    if (cached) {
      try { setAllData(JSON.parse(cached)); } catch (e) {}
    }
    fetchData();
  };

  const handleSuccess = (newEmployee) => {
    console.log("New employee created:", newEmployee);
    setModalVisible(false);
    fetchData();
  };

  const handleUpdateSuccess = async () => {
    setShowEditModal(false);
    fetchData();
  };

  const openWhatsApp = (phone) => {
    const phoneNumber = phone.replace("+", "");
    let message = "";

    if (subscriptionStatus) {
      if (subscriptionStatus.type === "expired") {
        message = t("helloSubscriptionExpired");
      } else if (subscriptionStatus.type === "today") {
        message = t("helloSubscriptionExpiresToday");
      } else if (subscriptionStatus.type === "expiring-soon") {
        message = t("helloSubscriptionExpiresSoon");
      }
    } else {
      message = t("helloInquire");
    }

    const url = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(
      message,
    )}`;
    window.open(url, "_blank");
  };

  const callPhone = (phone) => {
    window.location.href = `tel:${phone}`;
  };

  const sendEmail = () => {
    const subject = "Subscription Renewal Request";
    const body = "Hello, I would like to renew my subscription.";
    window.location.href = `mailto:info@neholtorem.com?subject=${encodeURIComponent(
      subject,
    )}&body=${encodeURIComponent(body)}`;
  };

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center h-full">
        <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="flex-1 h-full bg-gray-50 relative">
      {/* ✅ FULL BANNER FOR EXPIRED */}
      {subscriptionStatus && subscriptionStatus.type === "expired" && (
        <div className="flex items-center justify-center h-full p-6">
          <div
            className="bg-white rounded-2xl shadow-lg p-6 max-w-2xl w-full"
            style={{
              borderLeftWidth: "4px",
              borderLeftColor: subscriptionStatus.color,
            }}
          >
            {/* Header */}
            <div className="flex items-start mb-6">
              <div
                className="w-12 h-12 rounded-full flex items-center justify-center mr-4"
                style={{ backgroundColor: subscriptionStatus.color + "15" }}
              >
                <span className="text-2xl">{subscriptionStatus.icon}</span>
              </div>
              <div className="flex-1">
                <h2
                  className="text-xl font-bold mb-2"
                  style={{ color: subscriptionStatus.color }}
                >
                  {subscriptionStatus.title}
                </h2>
                <p className="text-gray-600">{subscriptionStatus.message}</p>
              </div>
            </div>

            {/* Divider */}
            <div className="border-t border-gray-200 my-6"></div>

            {/* Contact Section */}
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-4">
                {t("contactUsToRenew")}
              </h3>
              <div className="space-y-3">
                {/* Call Button */}
                <button
                  onClick={() => callPhone("+972527477128")}
                  className="w-full flex items-center bg-gray-50 hover:bg-gray-100 rounded-xl p-4 transition border border-gray-200"
                >
                  <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center mr-4">
                    <Phone className="w-5 h-5 text-blue-600" />
                  </div>
                  <div className="flex-1 text-left">
                    <p className="text-sm font-semibold text-gray-900">
                      {t("callUs")}
                    </p>
                    <p className="text-sm text-gray-600">+972 52-747-7128</p>
                  </div>
                </button>

                {/* WhatsApp Button */}
                <button
                  onClick={() => openWhatsApp("+972527477128")}
                  className="w-full flex items-center bg-gray-50 hover:bg-gray-100 rounded-xl p-4 transition border border-gray-200"
                >
                  <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center mr-4">
                    <MessageCircle className="w-5 h-5 text-green-600" />
                  </div>
                  <div className="flex-1 text-left">
                    <p className="text-sm font-semibold text-gray-900">
                      {t("chatOnWhatsApp")}
                    </p>
                    <p className="text-sm text-gray-600">+972 52-747-7128</p>
                  </div>
                </button>

                {/* Email Button */}
                <button
                  onClick={sendEmail}
                  className="w-full flex items-center bg-gray-50 hover:bg-gray-100 rounded-xl p-4 transition border border-gray-200"
                >
                  <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center mr-4">
                    <Mail className="w-5 h-5 text-purple-600" />
                  </div>
                  <div className="flex-1 text-left">
                    <p className="text-sm font-semibold text-gray-900">
                      {t("sendEmail")}
                    </p>
                    <p className="text-sm text-gray-600">info@neholtorem.com</p>
                  </div>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ✅ COMPACT BANNER FOR EXPIRING SOON / TODAY */}
      {subscriptionStatus &&
        (subscriptionStatus.type === "expiring-soon" ||
          subscriptionStatus.type === "today") && (
          <div className="px-4 pt-4">
            <button
              onClick={() => setShowContactModal(true)}
              className="w-full rounded-xl p-4 shadow-md transition-all hover:shadow-lg"
              style={{
                backgroundColor: subscriptionStatus.color + "15",
                borderLeftWidth: "4px",
                borderLeftColor: subscriptionStatus.color,
              }}
            >
              <div className="flex items-center gap-3">
                <span className="text-2xl">{subscriptionStatus.icon}</span>
                <div className="flex-1 text-left">
                  <h3
                    className="text-sm font-bold mb-1"
                    style={{ color: subscriptionStatus.color }}
                  >
                    {subscriptionStatus.title}
                  </h3>
                  <p className="text-xs text-gray-600">
                    {subscriptionStatus.message}
                  </p>
                </div>
                <ChevronDown
                  className="w-5 h-5"
                  style={{ color: subscriptionStatus.color }}
                />
              </div>
            </button>
          </div>
        )}

      {/* ✅ CONTACT MODAL */}
      {showContactModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-end justify-center z-50"
          onClick={() => setShowContactModal(false)}
        >
          <div
            className="bg-white rounded-t-3xl w-full max-w-lg p-6 animate-slide-up"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Handle */}
            <div className="w-10 h-1 bg-gray-300 rounded-full mx-auto mb-4"></div>

            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-bold text-gray-900">
                {t("contactToRenew")}
              </h2>
              <button
                onClick={() => setShowContactModal(false)}
                className="p-2 hover:bg-gray-100 rounded-lg transition"
              >
                <X className="w-5 h-5 text-gray-600" />
              </button>
            </div>

            {/* Phone Display */}
            <div className="flex items-center justify-center bg-gray-100 rounded-xl py-3 px-5 mb-6 gap-2">
              <Phone className="w-5 h-5 text-blue-600" />
              <span className="text-base font-semibold text-gray-900">
                +972 52-747-7128
              </span>
            </div>

            {/* Contact Options */}
            <div className="space-y-3 mb-6">
              {/* Call Button */}
              <button
                onClick={() => {
                  callPhone("+972527477128");
                  setShowContactModal(false);
                }}
                className="w-full flex items-center bg-blue-600 hover:bg-blue-700 text-white rounded-xl p-4 transition gap-3"
              >
                <div className="w-10 h-10 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
                  <Phone className="w-5 h-5" />
                </div>
                <div className="flex-1 text-left">
                  <p className="text-base font-semibold">{t("callUs")}</p>
                  <p className="text-sm opacity-80">{t("talkDirectly")}</p>
                </div>
                <ChevronDown className="w-5 h-5 rotate-[-90deg]" />
              </button>

              {/* WhatsApp Button */}
              <button
                onClick={() => {
                  openWhatsApp("+972527477128");
                  setShowContactModal(false);
                }}
                className="w-full flex items-center bg-green-600 hover:bg-green-700 text-white rounded-xl p-4 transition gap-3"
              >
                <div className="w-10 h-10 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
                  <MessageCircle className="w-5 h-5" />
                </div>
                <div className="flex-1 text-left">
                  <p className="text-base font-semibold">
                    {t("chatOnWhatsApp")}
                  </p>
                  <p className="text-sm opacity-80">{t("quickResponse")}</p>
                </div>
                <ChevronDown className="w-5 h-5 rotate-[-90deg]" />
              </button>

              {/* Email Button */}
              <button
                onClick={() => {
                  sendEmail();
                  setShowContactModal(false);
                }}
                className="w-full flex items-center bg-purple-600 hover:bg-purple-700 text-white rounded-xl p-4 transition gap-3"
              >
                <div className="w-10 h-10 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
                  <Mail className="w-5 h-5" />
                </div>
                <div className="flex-1 text-left">
                  <p className="text-base font-semibold">{t("sendEmail")}</p>
                  <p className="text-sm opacity-80">{t("sendMessage")}</p>
                </div>
                <ChevronDown className="w-5 h-5 rotate-[-90deg]" />
              </button>
            </div>

            {/* Footer Note */}
            <p className="text-xs text-gray-500 text-center italic">
              {t("weAreAvailable247")}
            </p>
          </div>
        </div>
      )}

      {/* SHOW NORMAL CONTENT ONLY IF NOT EXPIRED */}
      {(!subscriptionStatus || subscriptionStatus.type !== "expired") && (
        <>
          <BossShowEmployee
            onRefresh={fetchData}
            refreshing={loading}
            allData={allData}
            onAddEmployee={() => setModalVisible(true)}
            onEmployeeClick={handleEmployeeClick}
          />

          {selectedEmployee && (
            <div ref={statsRef}>
              <BossStatisticEmployeeData2
                key={selectedEmployee.id}
                onBack={handleBack}
                onEmployeeUpdate={(updated) => {
                  setAllData((prev) => {
                    if (!prev?.myEmployees) return prev;
                    return {
                      ...prev,
                      myEmployees: prev.myEmployees.map((e) =>
                        e.id === updated.id ? { ...e, ...updated } : e
                      ),
                    };
                  });
                }}
              />
            </div>
          )}

          {/* Add Employee Button */}
          <button
            onClick={() => setModalVisible(true)}
            className={`fixed bottom-6 ${isRTL ? "left-6" : "right-6"} w-14 h-14 bg-blue-600 hover:bg-blue-700 text-white rounded-full shadow-lg flex items-center justify-center transition-all hover:scale-110 z-50`}
          >
            <Plus className="w-7 h-7" />
          </button>

          {/* Show Inactive Employees Button */}
          <button
            onClick={() => setInactiveModalVisible(true)}
            className={`fixed bottom-6 ${isRTL ? "left-24" : "right-24"} w-14 h-14 bg-red-500 hover:bg-red-600 text-white rounded-full shadow-lg flex items-center justify-center transition-all hover:scale-110 z-50`}
          >
            <EyeOff className="w-6 h-6" />
          </button>

          {/* Create Employee Modal */}
          <BossCreateEmployee
            visible={modalVisible}
            onClose={() => setModalVisible(false)}
            onSuccess={handleSuccess}
          />

          {/* Inactive Employees Modal */}
          <BossEmployeesNotActive
            visible={inactiveModalVisible}
            onClose={() => setInactiveModalVisible(false)}
            onRefresh={fetchData}
            allData={allData}
          />

          {/* Update Employee Modal */}
          <BossUpdateEmployeeModal
            visible={showEditModal}
            onClose={() => setShowEditModal(false)}
            employee={choosedEmployee}
            onUpdateSuccess={handleUpdateSuccess}
          />
        </>
      )}

      <style>{`
        @keyframes slide-up {
          from { transform: translateY(100%); }
          to { transform: translateY(0); }
        }
        .animate-slide-up {
          animation: slide-up 0.3s ease-out;
        }
      `}</style>
    </div>
  );
}
